import { useState } from "react";
import { AppSidebar } from "@/components/app-sidebar";
import { Button } from "@/components/ui/button";
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, signOut } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { toast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { ChevronLeft, LockIcon, SaveIcon, Settings as SettingsIcon } from "lucide-react";
import { useHideDeactivatedUsers } from "@/hooks/use-hide-deactivated-users";

// Add password validation function
const validatePassword = (password: string): { isValid: boolean; message?: string } => {
  if (password.length < 8) {
    return { isValid: false, message: "Das Passwort muss mindestens 8 Zeichen lang sein." };
  }
  
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, message: "Das Passwort muss mindestens einen Großbuchstaben enthalten." };
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return { isValid: false, message: "Das Passwort muss mindestens ein Sonderzeichen enthalten." };
  }
  
  return { isValid: true };
};

export default function Settings() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isAdmin = currentUser?.role === "admin";
  const [hideDeactivated, setHideDeactivated] = useHideDeactivatedUsers();

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    // Validate inputs
    if (!currentPassword) {
      setError("Bitte geben Sie Ihr aktuelles Passwort ein.");
      return;
    }
    
    if (!newPassword) {
      setError("Bitte geben Sie ein neues Passwort ein.");
      return;
    }

    // Validate new password
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message);
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError("Die neuen Passwörter stimmen nicht überein.");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const user = auth.currentUser;
      
      if (!user || !user.email) {
        setError("Kein Benutzer angemeldet.");
        setIsLoading(false);
        return;
      }
      
      // Re-authenticate the user
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Change password
      await updatePassword(user, newPassword);
      
      // Clear form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      
      toast({
        title: "Erfolg",
        description: "Ihr Passwort wurde erfolgreich geändert.",
      });
      
    } catch (error: any) {
      console.error("Fehler beim Ändern des Passworts:", error);
      
      if (error.code === 'auth/wrong-password') {
        setError("Das aktuelle Passwort ist falsch.");
      } else if (error.code === 'auth/weak-password') {
        setError("Das neue Passwort ist zu schwach.");
      } else if (error.code === 'auth/requires-recent-login') {
        setError("Bitte melden Sie sich erneut an, um Ihr Passwort zu ändern.");
        setTimeout(() => {
          signOut(auth);
          navigate("/login");
        }, 3000);
      } else {
        setError("Fehler beim Ändern des Passworts: " + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <AppSidebar />
      <div className="flex-1 overflow-hidden flex flex-col">
        <div className="py-4 px-6 border-b">
          <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
            <div>
              <h1 className="text-2xl font-bold">Einstellungen</h1>
              <p className="text-sm text-muted-foreground">Verwalten Sie Ihre Konto-Einstellungen</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid gap-6 max-w-xl mx-auto lg:mx-0">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <LockIcon className="h-5 w-5 text-muted-foreground" />
                  <h2 className="text-xl font-semibold">Passwort ändern</h2>
                </div>
                
                <form onSubmit={handlePasswordChange} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Aktuelles Passwort</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full"
                      placeholder="Ihr aktuelles Passwort"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">Neues Passwort</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full"
                      placeholder="Neues Passwort"
                    />
                    <p className="text-xs text-muted-foreground">
                      Das Passwort muss mindestens 8 Zeichen lang sein und mindestens einen Großbuchstaben sowie ein Sonderzeichen enthalten.
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Neues Passwort bestätigen</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full"
                      placeholder="Passwort wiederholen"
                    />
                  </div>
                  
                  {error && (
                    <div className="text-sm text-destructive">{error}</div>
                  )}
                  
                  <Button 
                    type="submit" 
                    className="w-full mt-4 flex items-center gap-2"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      "Passwort wird geändert..."
                    ) : (
                      <>
                        <SaveIcon className="h-4 w-4" />
                        Passwort ändern
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {isAdmin && (
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <SettingsIcon className="h-5 w-5 text-muted-foreground" />
                    <h2 className="text-xl font-semibold">Anzeige</h2>
                  </div>

                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <Label htmlFor="hideDeactivatedToggle" className="text-base">
                        Deaktivierte Nutzer in Dropdowns ausblenden
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        Wenn aktiviert, erscheinen deaktivierte Nutzer nicht mehr in
                        Auswahlmenüs zur Lead-Zuweisung. Sie bleiben in der
                        Nutzerverwaltung weiterhin sichtbar.
                      </p>
                    </div>
                    <Switch
                      id="hideDeactivatedToggle"
                      checked={hideDeactivated}
                      onCheckedChange={setHideDeactivated}
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

